import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import PageHeader from "../components/Layout/PageHeader";
import LoadingSpinner from "../components/common/LoadingSpinner";
import CardTable from "../components/charts/CardTable";
import SEO from "../components/SEO";
import ToolSchema from "../components/seo/ToolSchema";
import { getRacesToday, getRaceCard, getBetfairPrices, getPriceSnapshots } from "../api";
import useIsMobile from "../hooks/useIsMobile";
import { todayStr, yesterdayStr } from "../hooks/dateUtils";

function getDayLabel(dateStr) {
  const today = todayStr();
  const yesterday = yesterdayStr();
  const d = dateStr?.slice(0, 10);
  if (d === today) return "Today";
  if (d === yesterday) return "Yesterday";
  return d || "Unknown";
}

function groupByDateAndVenue(races) {
  const byDate = {};
  races.forEach((r) => {
    const label = getDayLabel(r.race_date);
    if (!byDate[label]) byDate[label] = {};
    if (!byDate[label][r.course_name]) byDate[label][r.course_name] = [];
    byDate[label][r.course_name].push(r);
  });
  const order = ["Today", "Yesterday"];
  return order
    .filter((label) => byDate[label])
    .map((label) => ({
      label,
      venues: Object.entries(byDate[label])
        .map(([name, list]) => ({
          name,
          races: list.sort((a, b) => a.race_time.localeCompare(b.race_time)),
        }))
        .sort((a, b) => a.races[0].race_time.localeCompare(b.races[0].race_time)),
    }));
}

// Landing intro shown before a race is picked — mirrors the Tissue Pricer's
// intro: a shot of the card, the one-paragraph "what it is", and links to the
// full guide and the downloadable mind-map.
function TodaysCardsIntro() {
  return (
    <div className="tp-intro">
      <div className="tp-intro-body">
        <h3>What it is</h3>
        <p>
          A detailed racecard for every race, pulling each runner's historical
          strike rates, going suitability and live exchange prices into one
          view — the context behind the numbers, alongside the Tissue Pricer's
          model price.
        </p>
        <div className="tp-intro-actions">
          <Link to="/help/todays-cards" className="tp-intro-btn">
            Read the full guide →
          </Link>
          <a
            href="/downloads/todays-cards-how-to-use.pdf"
            className="tp-intro-link"
            download
          >
            Download the mind-map (PDF)
          </a>
          <Link to="/book" className="tp-intro-link">
            The book: Mispriced →
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function TodaysCard() {
  const [races, setRaces] = useState([]);
  const [selectedRace, setSelectedRace] = useState(null);
  const [cardData, setCardData] = useState(null);
  const [raceInfo, setRaceInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedVenues, setExpandedVenues] = useState({});
  const [prices, setPrices] = useState({});
  const [priceHistory, setPriceHistory] = useState({});
  const [pricesUpdatedAt, setPricesUpdatedAt] = useState(null);
  const isMobile = useIsMobile();
  const contentRef = useRef(null);

  const toggleVenue = (name) =>
    setExpandedVenues((prev) => ({ ...prev, [name]: !prev[name] }));

  useEffect(() => {
    getRacesToday()
      .then((data) => {
        setRaces(data);
        setExpandedVenues({});
      })
      .catch(() => setError("Failed to load today's races."));
  }, []);

  const loadCard = useCallback(async (raceId) => {
    setLoading(true);
    setError(null);
    setPrices({});
    setPriceHistory({});
    try {
      const data = await getRaceCard(raceId);
      setCardData(data.runners || []);
      setRaceInfo(data.race || null);
    } catch (err) {
      console.error(err);
      setError("Failed to load racecard data.");
    }
    setLoading(false);

    // Fetch cached Betfair prices (non-blocking)
    getBetfairPrices(raceId)
      .then((data) => {
        setPrices(data.prices || {});
        setPricesUpdatedAt(data.updated_at || null);
      })
      .catch(() => {});

    // Fetch intraday price history for the sparkline (non-blocking)
    getPriceSnapshots(raceId)
      .then((data) => setPriceHistory(data.history || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedRace) loadCard(selectedRace);
  }, [selectedRace, loadCard]);

  // Scroll to content on mobile after loading
  useEffect(() => {
    if (isMobile && !loading && selectedRace && contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [isMobile, loading, selectedRace, cardData]);

  const days = groupByDateAndVenue(races);

  const renderContent = () => (
    <>
      {error && (
        <div style={{
          padding: "1.5rem", background: "#fef2f2",
          border: "1px solid #fca5a5", borderRadius: 8,
          marginBottom: "1rem", color: "#991b1b",
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : cardData && raceInfo ? (
        <>
          {/* Race info bar */}
          <div className="race-info-bar">
            <div className="venue">{raceInfo.course_name}</div>
            <div className="details">
              {raceInfo.race_time} &middot; {raceInfo.race_type} &middot; {raceInfo.distance}
              {raceInfo.class_num && <> &middot; Class {raceInfo.class_num}</>}
            </div>
            <div className="going-badge">{raceInfo.going_desc}</div>
          </div>

          {/* Legend */}
          <div className="card-legend">
            <div className="card-legend-group">
              <span className="card-legend-swatch" style={{ background: "#5c8a5e", color: "#fff" }}>Strong</span>
              <span className="card-legend-swatch" style={{ background: "#cdb87a", color: "#fff" }}>Neutral</span>
              <span className="card-legend-swatch" style={{ background: "#b85c3c", color: "#fff" }}>Weak</span>
            </div>
          </div>

          {/* Card table */}
          <CardTable runners={cardData} race={raceInfo} prices={prices} priceHistory={priceHistory} />

          {/* Footnote */}
          <p className="bias-footnote" style={{ marginTop: 16 }}>
            SR% = overall strike rate (runs shown in brackets).
            Going score from historical going records.
            * = estimated from adjacent conditions. Colour intensity reflects strength of signal.
            {pricesUpdatedAt && (
              <> Prices updated {new Date(pricesUpdatedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}.</>
            )}
          </p>
        </>
      ) : null}
    </>
  );

  return (
    <>
      <SEO title="Today's Card" description="Today's Cards — today's full UK horse racing card with going, draw, trainer and jockey data in one place. Free on Nigel's Notebook." path="/tools/todays-card" />
      <ToolSchema
        name="Today's Card"
        description="Today's Cards — today's full UK horse racing card with going, draw, trainer and jockey data in one place. Free on Nigel's Notebook."
        path="/tools/todays-card"
      />
      <PageHeader
        title="Today's Card"
        subtitle="At-a-glance scores for every runner — radar, jockey, trainer and going."
      />
      <div className="container-wide" style={{ paddingTop: "1rem", paddingBottom: 0 }}>
        <Link to="/help/todays-cards" style={{ color: "var(--terracotta)", fontWeight: 600, fontSize: "0.9rem", textDecoration: "none" }}>
          New here? How to use Today's Cards →
        </Link>
      </div>
      <div className="container-wide" style={{ paddingTop: "1.5rem", paddingBottom: "3rem" }}>
        <div className="tool-layout">
          {/* Sidebar: race picker */}
          <div>
            <div className="filter-panel">
              <div className="filter-panel-header">
                <h3>Races</h3>
              </div>
              {days.length === 0 && !error && (
                <p style={{ padding: "12px", fontSize: 13, color: "var(--pencil)" }}>
                  No races found.
                </p>
              )}
              {days.map((day) => (
                <div key={day.label}>
                  <div style={{
                    padding: "8px 12px 4px",
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    color: "var(--pencil)",
                    letterSpacing: "0.05em",
                  }}>
                    {day.label}
                  </div>
                  {day.venues.map((venue) => {
                    const key = `${day.label}:${venue.name}`;
                    const isExpanded = !!expandedVenues[key];
                    return (
                      <div key={key} className="race-picker-group">
                        <button
                          className="race-picker-venue"
                          onClick={() => toggleVenue(key)}
                        >
                          <span className={`venue-chevron${isExpanded ? " open" : ""}`}>&#9654;</span>
                          <span>{venue.name}</span>
                          <span className="venue-count">{venue.races.length}</span>
                        </button>
                        {isExpanded && venue.races.map((r) => (
                          <React.Fragment key={r.race_id}>
                            <button
                              className={`race-picker-btn${selectedRace === r.race_id ? " active" : ""}`}
                              onClick={() => {
                                setSelectedRace(r.race_id);
                                setExpandedVenues((prev) => ({ ...prev, [key]: true }));
                              }}
                            >
                              <span className="race-time">{r.race_time}</span>
                              {" "}{r.race_type} {r.distance}
                            </button>
                            {isMobile && selectedRace === r.race_id && (
                              <div ref={contentRef} className="mobile-inline-content">
                                {renderContent()}
                              </div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Mobile: intro shown until a race is picked */}
          {isMobile && !selectedRace && <TodaysCardsIntro />}

          {/* Main content (desktop only) */}
          {!isMobile && (
            <div>
              {renderContent()}
              {!selectedRace && (
                <>
                  <TodaysCardsIntro />
                  <p className="empty-state-prompt">
                    Pick a race from the list to see colour-coded scores for every runner.
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
